import { motion } from 'framer-motion'

/**
 * Post-verification success confirmation.
 *
 * Yellow disc pops in via spring, tick draws over it, caption fades
 * up. `active` gates the animation so it only plays when the panel
 * actually mounts / becomes visible - passing `false` renders the
 * static empty state.
 *
 * Shared across sign-in and every identifier verification / update
 * flow under `/account`.
 */
export function VerificationSuccessPanel({
  active,
  caption = 'Verified!',
}: {
  active: boolean
  caption?: string
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-6 py-8">
      <svg
        width="96"
        height="96"
        viewBox="0 0 100 100"
        aria-hidden="true"
        className="text-foreground"
      >
        <motion.circle
          cx="50"
          cy="50"
          className="fill-primary"
          initial={{ r: 0 }}
          animate={{ r: active ? 46 : 0 }}
          transition={{ type: 'spring', duration: 0.5, bounce: 0.45 }}
        />
        <motion.path
          d="M28 52 L44 68 L72 36"
          fill="none"
          stroke="currentColor"
          strokeWidth="8"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: active ? 1 : 0 }}
          transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1], delay: active ? 0.25 : 0 }}
        />
      </svg>

      <motion.p
        className="text-headline-md"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: active ? 1 : 0, y: active ? 0 : 8 }}
        transition={{ duration: 0.3, ease: 'easeOut', delay: active ? 0.55 : 0 }}
      >
        {caption}
      </motion.p>
    </div>
  )
}

/** How long the success panel stays visible before the caller navigates. */
export const VERIFICATION_SUCCESS_HOLD_MS = 2500
